<template>
  <div class="push-notification-toggle">
    <label class="toggle-switch" :title="label">
      <input
        type="checkbox"
        v-model="isSubscribed"
        :disabled="!isSupported || loading"
        @change="toggleSubscription"
      />
      <span class="slider"></span>
      <span class="toggle-label">
        <span v-if="loading" class="spinner"></span>
        <span :class="['icon', iconClass]">{{ icon }}</span>
        <span class="label-text">{{ label }}</span>
      </span>
    </label>

    <button
      v-if="statusMessage"
      type="button"
      class="push-info-btn"
      :aria-label="statusMessage"
      :aria-expanded="showInfo ? 'true' : 'false'"
      @click.stop="showInfo = !showInfo"
    >
      <i class="mdi mdi-information-outline" />
    </button>
    <div v-if="statusMessage && showInfo" class="push-tooltip" @click.stop>
      {{ statusMessage }}
    </div>
  </div>
</template>

<script>
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush
} from "@/api/PushNotifications.js";

export default {
  name: "BasePushNotificationToggle",
  data() {
    return {
      isSubscribed: false,
      isSupported: false,
      permissionDenied: false,
      loading: false,
      error: null,
      showInfo: false
    };
  },
  computed: {
    statusMessage() {
      if (!this.isSupported) {
        return "Push notifications not supported in this browser.";
      }
      if (this.permissionDenied) {
        return "Notifications blocked. Enable them in browser settings.";
      }
      return "";
    },
    label() {
      if (!this.isSupported || this.permissionDenied) return "Push OFF";
      return this.isSubscribed ? "Push ON" : "Push OFF";
    },
    icon() {
      if (!this.isSupported || this.permissionDenied) return "🔕";
      return this.isSubscribed ? "🔔" : "🔕";
    },
    iconClass() {
      if (!this.isSupported || this.permissionDenied) return "disabled";
      return this.isSubscribed ? "active" : "";
    }
  },
  watch: {
    showInfo(open) {
      if (open) {
        document.addEventListener("click", this.closeInfo, true);
      } else {
        document.removeEventListener("click", this.closeInfo, true);
      }
    },
    statusMessage(message) {
      if (!message) this.showInfo = false;
    }
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeInfo, true);
  },
  async mounted() {
    this.isSupported = isPushSupported();
    if (this.isSupported) {
      const permission = getNotificationPermission();
      this.permissionDenied = permission === "denied";
      if (permission !== "denied") {
        this.isSubscribed = await isSubscribedToPush();
      }
    }
  },
  methods: {
    closeInfo(event) {
      if (!event || !this.$el.contains(event.target)) {
        this.showInfo = false;
      }
    },
    async toggleSubscription() {
      this.loading = true;
      this.error = null;
      const newValue = this.isSubscribed;
      try {
        if (newValue) {
          await subscribeToPush();
          this.$emit("subscribed");
          this.$toast.success("Push notifications enabled", {
            position: "top-right",
            duration: 3000
          });
        } else {
          await unsubscribeFromPush();
          this.$emit("unsubscribed");
          this.$toast.info("Push notifications disabled", {
            position: "top-right",
            duration: 3000
          });
        }
      } catch (err) {
        this.error = err.message || "Failed to update notification settings";
        this.isSubscribed = !newValue;
        if (err.message?.includes("denied")) this.permissionDenied = true;
        this.$toast.error(this.error, {
          position: "top-right",
          duration: 5000
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.push-notification-toggle {
  display: flex;
  align-items: center;
  padding: 0 10px;
  position: relative;
}

.toggle-switch {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.toggle-switch input {
  display: none;
}

.slider {
  width: 32px;
  height: 18px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 18px;
  position: relative;
  transition: background-color 0.3s;
  margin-right: 6px;
  flex-shrink: 0;
}

.slider::before {
  content: "";
  position: absolute;
  width: 14px;
  height: 14px;
  background-color: white;
  border-radius: 50%;
  top: 2px;
  left: 2px;
  transition: transform 0.3s;
}

input:checked + .slider {
  background-color: #4caf50;
}

input:checked + .slider::before {
  transform: translateX(14px);
}

input:disabled + .slider {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-label {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.85);
  gap: 3px;
}

.icon.active {
  animation: ring 0.5s ease;
}

.icon.disabled {
  opacity: 0.5;
}

.label-text {
  white-space: nowrap;
}

.spinner {
  width: 10px;
  height: 10px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.push-info-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  padding: 0;
  background: transparent;
  border: 0;
  outline: none;
  color: inherit;
  opacity: 0.7;
  cursor: pointer;
}

.push-info-btn:hover {
  opacity: 1;
}

.push-info-btn i {
  font-size: 16px;
}

.push-tooltip {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  left: auto;
  background: #333;
  color: white;
  font-size: 11px;
  line-height: 1.35;
  padding: 6px 8px;
  border-radius: 4px;
  width: max-content;
  max-width: 200px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
  z-index: 100;
}

@keyframes ring {
  0%,
  100% {
    transform: rotate(0);
  }
  25% {
    transform: rotate(15deg);
  }
  75% {
    transform: rotate(-15deg);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
